import { Schema, Block } from 'slate';

import {
  NODE_TYPE as NT,
  // MARK_TYPE as MT,
  // cleanTopLevelBlockTypes,
} from 'shared/constants/slate';
import {
  stripWhitespacesAtStartOfCodeLine,
  stripExtraWhitespacesInCodeLine,
  stripWhitespacesAtEndOfCodeLine,
} from '../rules';
import { editListOpts } from '../plugins';
import unwrapList from '../plugins/EditList/changes/unwrapList';

import { toggleMarkWorkaround } from './toggleMarkWorkaround';
import { isSelTouchingLastBlock } from './isSelTouchingLastBlock';
import { topLevelBlocksInSel } from './topLevelBlocksInSel';
import { getMarks } from './getMarks';


const makeNoChange = state => state.change();

// Not used..
const isMarkPresent = (state, type) => (
  state.marks.some(mark => mark.type === type)
);

// Not used..
const isBlockPresent = (state, type) => (
  state.blocks.some(node => node.type === type)
);

const hasActiveMark = (state, type) => (
  state.activeMarks.map(m => m.type).includes(type)
);
const isInlinePresent = (state, type) => (
  state.inlines.some(inline => inline.type === type)
);


const isDeleting = key => (
  ['backspace', 'delete'].includes(key)
);

const applyDeleteKeyDown = (_, data, change) => {
  let boundary = 'Char';
  if (data.isWord) boundary = 'Word';
  if (data.isLine) boundary = 'Line';

  switch (data.key) {
    case 'backspace':
      return change['delete' + boundary + 'Backward']();
    case 'delete':
      return change['delete' + boundary + 'Forward']();
    default:
      return;
  }
};

// No longer used.. delete?
const isOffsetFromZeroToZero = (state) => (
  state.startOffset === 0 && state.endOffset === 0
);

const isFromStartToStartOfBlock = (state) => {
  if (state.blocks.size < 1) {
    // Should never happen..?
    return false;
  }

  const { selection: sel, startBlock, endBlock } = state;
  return (
    sel.hasStartAtStartOf(startBlock)
    && sel.hasEndAtStartOf(endBlock)
  );
};


const compileRulesFromPlugins = (plugins = []) => {
  return plugins.reduce(
    (acc, plugin) => {
      const { schema = {} } = plugin;
      const { rules = [] } = schema;
      return [ ...acc, ...rules, ];
    },
    []
  );
};


const isBlockOfTargetType = (block, type) => {
  switch (type) {
    case NT.headingOne:
    case NT.headingTwo:
      const [ headingType, targetLevel ] = type.split('-');
      const level = block.data.get('level');

      return (
        block.type === headingType
        && level
        && level.toString() === targetLevel
      );

    default:
      // Everything else..
      return block.type === type;
  }
};


const adjustSelExcludeLastBlock = (change) => {
  const { state } = change;

  if (state.blocks.size < 2) {
    return change;
  }

  if (state.isBackward) {
    change.flip();
  }

  const secondLastBlock = state.blocks.pop().last();
  return change.extendToEndOf(secondLastBlock);
};



const unsetTopBlocksInSel = (change) => {
  const { state } = change;

  const whitespaceRulesSchema = Schema.create({
    rules: [
      // Important to check in these orders!
      // For start and end check, we specifically
      // look for offset at either end so regex
      // match for middle doesn't get confused.
      stripWhitespacesAtStartOfCodeLine,
      stripWhitespacesAtEndOfCodeLine,
      stripExtraWhitespacesInCodeLine,
    ],
  });

  topLevelBlocksInSel(state).forEach(block => {
    switch (block.type) {
      case NT.paragraph:
        // Already default block, nothing to unset.
        break;

      case NT.heading:
      case NT.blockquote:
        change.unwrapBlockByKey(block.key);
        break;

      case NT.codeBlock:
        // Turn inner child into paragraph while making
        // sure paragraphs comform to schema rules.
        block.nodes.forEach(node => (
          change
            .normalizeNodeByKey(node.key, whitespaceRulesSchema)
            .setNodeByKey(node.key, NT.paragraph)
        ));

        // Merge each of the paragraphs into one
        block.nodes.reverse().butLast().forEach(node => {
          change.mergeNodeByKey(node.key, { normalize: false });
        });

        // Unwrap code block to expose the inner merged paragraph.
        change.unwrapBlockByKey(block.key);
        break;

      case NT.ul:
      case NT.ol:
        // XXX: Since we are limiting to ONLY 1 level depth
        // in list, we can just unwrap list items in selection.
        unwrapList(editListOpts, change);
        break;

      default:
        // ¯\_(ツ)_/¯
        break;
    }
  });

  return change;
};


const transformTopBlocksInSel = (change, type) => {
  const level = {
    [NT.headingOne]: 1,
    [NT.headingTwo]: 2,
  }[type] || null;

  switch (type) {
    case NT.headingOne:
    case NT.headingTwo:
      topLevelBlocksInSel(change.state).forEach(block => (
        change.wrapBlockByKey(block.key, {
          type: NT.heading,
          data: { level },
        })
      ));
      break;

    case NT.blockquote:
      change.wrapBlock(NT.blockquote);
      break;

    default:
      // TODO: Heading and Blockquote are currently
      // the only types available for transformation
      // over selection. Handle other types later..
      return;
  }

  return change;
};


const onlyInTopLevelBlock = (value, onlyIn = []) => {
  const { document: doc, startBlock } = value;

  if (startBlock && doc.getDepth(startBlock.key) > 1) return false;
  if (Array.isArray(onlyIn)) {
    if (startBlock && !onlyIn.includes(startBlock.type)) return false;
  }

  return true;
};

const onlyInTopLevelParagraph = (value) => {
  return onlyInTopLevelBlock(value, [NT.paragraph])
};

const onlyCollapsedInTopLevelParagraph = (value) => {
  if (!onlyInTopLevelParagraph(value)) return false;
  if (value.isExpanded) return false;

  return true;
};

const onlyCollapsedInTopLevelParagraphAndEmpty = (value) => {
  if (!onlyCollapsedInTopLevelParagraph(value)) return false;
  const { startBlock } = value;
  if (!startBlock || !startBlock.isEmpty) return false;

  return true;
};

// const onlyInHeadingCollapsedAndEmpty = (value) => {
//   const { startBlock, isExpanded } = value;
//
//   if (!onlyInTopLevelBlock(value, [NT.heading])) return false;
//   if (isExpanded) return false;
//   if (!startBlock || !startBlock.isEmpty) return false;
//
//   return true;
// };


const getIfCollapsedInTargetBlockType = (change, onlyIn = []) => {
  const { value } = change;
  const { isCollapsed, startBlock } = value;

  if (!isCollapsed) return;
  if (!startBlock) return;

  if (Array.isArray(onlyIn) && onlyIn.includes(startBlock.type)) {
    return startBlock;
  }
}

const insertNodeAfterTarget = (change, target, typeOrProps = NT.paragraph) => {
  const { document: doc } = change.value;
  const tlb = doc.getFurthestBlock(target.key) || target;
  const index = doc.nodes.findIndex(n => n.key === tlb.key);

  const p = Block.create(typeOrProps);
  return change.insertNodeByKey(doc.key, index+1, p);
};


// Similar to ensureNoOrphanReceiver validation rule, but as util func.
// If focused but moved out of receiver then turn it into a normal paragraph.
const handleOrphanReceiverNode = (node, editor) => {
  const { value } = editor;
  const { isFocused, isCollapsed, selection: sel } = value;

    const isReceiving = node.data.get('isReceiving');
  const isReceivable = isCollapsed && sel.hasEdgeIn(node);

  if (isFocused && !isReceivable && isReceiving) {
    const change = value.change()
      .setNodeByKey(node.key, NT.default)

    return editor.onChange(change);
  }
}

// There are several onDrag* events and onDragStart seems to work..
// This is for HandleImage plugin, to allow onDrop event to
// identify which node was dragged and dropped.
const transferWithSlateNodeKey = (key) => (event) => {
  event.dataTransfer.setData("key", key);
};


export { makeNodeProps } from './makeNodeProps';

export {
  makeNoChange,
  isMarkPresent,
  isBlockPresent,
  isInlinePresent,
  hasActiveMark,
  isDeleting,
  applyDeleteKeyDown,
  isOffsetFromZeroToZero,
  isFromStartToStartOfBlock,
  isSelTouchingLastBlock,
  getMarks,
  compileRulesFromPlugins,
  isBlockOfTargetType,
  adjustSelExcludeLastBlock,
  topLevelBlocksInSel,
  unsetTopBlocksInSel,
  transformTopBlocksInSel,
  toggleMarkWorkaround,

  getIfCollapsedInTargetBlockType,
  insertNodeAfterTarget,
  handleOrphanReceiverNode,

  transferWithSlateNodeKey,

  // onlyInHeadingCollapsedAndEmpty,
  onlyInTopLevelParagraph,
  onlyCollapsedInTopLevelParagraph,
  onlyCollapsedInTopLevelParagraphAndEmpty,
}



// WEBPACK FOOTER //
// ./src/components/slate/utils/index.js